import { z } from 'zod';
import type { HelpdeskSessionResponse } from './widget-session';

const message = z.discriminatedUnion('type', [
  z.object({ type: z.literal('move-helpdesk-ready'), requestId: z.uuid() }).strict(),
  z.object({
    type: z.literal('move-helpdesk-connected'),
    requestId: z.uuid(),
    installationOrigin: z.url(),
    session: z.object({
      accessToken: z.string(), accessExpiresAt: z.string(),
      refreshToken: z.string(), refreshExpiresAt: z.string(),
    }).strict(),
  }).strict(),
  z.object({ type: z.literal('move-helpdesk-error'), requestId: z.uuid() }).strict(),
]);

/** Open the first-party connection page and accept credentials only from that exact popup. */
export function connectMovePopup(options: {
  moveWebOrigin: string;
  installationOrigin: string;
  locale: 'en' | 'ru';
}): Promise<HelpdeskSessionResponse> {
  const requestId = crypto.randomUUID();
  const url = new URL(`/${options.locale}/helpdesk/connect`, options.moveWebOrigin);
  url.searchParams.set('requestId', requestId);
  url.searchParams.set('installationOrigin', options.installationOrigin);
  const popup = window.open(url.href, 'move-helpdesk-connect', 'popup,width=560,height=720');
  if (!popup) return Promise.reject(new Error('The sign-in popup was blocked.'));
  const openedPopup = popup;
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => { finish(new Error('The sign-in popup timed out.')); }, 10 * 60_000);
    const closed = window.setInterval(() => {
      if (openedPopup.closed) { finish(new Error('The sign-in popup was closed.')); }
    }, 500);
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== options.moveWebOrigin || event.source !== openedPopup) return;
      const parsed = message.safeParse(event.data);
      if (!parsed.success || parsed.data.requestId !== requestId) return;
      if (parsed.data.type === 'move-helpdesk-ready') {
        openedPopup.postMessage(
          { type: 'move-helpdesk-connect', requestId, installationOrigin: options.installationOrigin },
          options.moveWebOrigin
        );
        return;
      }
      if (parsed.data.type === 'move-helpdesk-connected' &&
          parsed.data.installationOrigin === options.installationOrigin) {
        finish(undefined, parsed.data.session);
      } else if (parsed.data.type === 'move-helpdesk-error') {
        finish(new Error('Move connection failed. Please try again.'));
      }
    };
    function finish(error?: Error, session?: HelpdeskSessionResponse) {
      window.clearTimeout(timeout);
      window.clearInterval(closed);
      window.removeEventListener('message', onMessage);
      if (error) reject(error);
      else if (session) resolve(session);
      if (!openedPopup.closed) openedPopup.close();
    }
    window.addEventListener('message', onMessage);
  });
}
